import { FC } from 'react';
import styled from 'styled-components';

import ScrollToTop from '@/components/Buttons/ScrollToTop';
import Container from '@/components/Container/Container';
import Footer from '@/components/Footer/Footer';
import FooterBanner from '@/components/FooterBanner/FooterBanner';
import Navigation from '@/components/Navigation/Navigation';
import Seo from '@/components/Seo/Seo';
import { BLOG_POSTS_API_LIMIT, LANGUAGES } from '@/lib/constants';
import { fetchAPI } from '@/lib/fetchAPI';
import { translate } from '@/lib/translate';
import Post from '@/modules/blog/PostItem';
import Recommended from '@/modules/blog/Recommended';
import Tags from '@/modules/blog/Tags';

const TagPage: FC<any> = ({ url, lang, recommended, tag, tags }) => (
	<>
		<Seo
			url={`/blog/tag/${url}`}
			title="Blog with Tags"
			description="Blog with Tags"
		/>

		<Navigation lang={lang} />

		<Wrapper>
			<Container>
				<Heading>
					<Left>
						<Title>
							{translate(lang, { en: 'Blog', ru: 'Блог', ua: 'Блог' })}
						</Title>

						<Description>
							with tag&nbsp;
							{tag?.tag}
						</Description>
					</Left>

					<div>
						<div />
					</div>
				</Heading>

				<Content>
					<Row>
						{tag?.blogPosts?.map((post, index) => (
							<Column key={`tag_col_${index}`}>
								<Post data={post} />
							</Column>
						))}
					</Row>

					<Sidebar>
						<Recommended data={recommended} />

						<Tags data={tags} />
					</Sidebar>
				</Content>

				<ScrollToTop />
			</Container>
		</Wrapper>

		<FooterBanner
			title="Want to get started?"
			cta={{ text: 'Subscribe', link: '/subscribe' }}
			image={{
				alt: 'Phone',
				link: '/images/footer-banner/phone.png',
			}}
		/>

		<Footer lang={lang} />
	</>
);

const Wrapper = styled.div`
	margin-top: 140px;
	margin-bottom: 80px;

	@media (max-width: 1024px) {
		margin-bottom: 140px;
	}

	@media (max-width: 800px) {
		margin-bottom: 80px;
		margin-top: 115px;
	}

	@media (max-width: 660px) {
		margin-top: 90px;
	}
`;

const Heading = styled.div`
	margin-bottom: 54px;
	display: flex;

	@media (max-width: 800px) {
		margin-bottom: 44px;
	}

	@media (max-width: 660px) {
		margin-bottom: 36px;
	}
`;

const Left = styled.div`
	display: flex;
	align-items: center;
`;

const Title = styled.h1`
	font-family: var(--font-sans);

	font-weight: var(--font-bold);
	font-size: 65px;
	line-height: 60px;
	color: var(--color-black);

	@media (max-width: 800px) {
		font-size: 40px;
	}
`;

const Description = styled.div`
	font-family: var(--font-sans);

	font-size: 28px;
	line-height: 25px;
	color: var(--color-black);
	margin-top: 26px;
	margin-left: 20px;

	@media (max-width: 800px) {
		font-size: 20px;
		margin-top: 4px;
	}
`;

const Row = styled.div`
	display: flex;
	align-items: flex-start;
	flex-wrap: wrap;
	justify-content: space-between;
	width: 100%;

	@media (max-width: 1024px) {
		justify-content: space-between;
	}
`;

const Content = styled.div`
	display: flex;

	@media (max-width: 800px) {
		flex-direction: column;
	}
`;

const Sidebar = styled.div`
	width: 40%;

	@media (max-width: 1137px) {
		width: 55%;
	}

	@media (max-width: 800px) {
		width: 100%;
	}
`;

const Column = styled.div`
	max-width: calc(50% - 60px);
	margin-right: 60px;
	margin-bottom: 50px;

	@media (max-width: 1024px) {
		width: calc(50% - 40px);
		width: 400px;
		max-width: calc(50% - 40px);
		margin-right: 30px;
	}

	@media (max-width: 800px) {
		max-width: calc(50% - 20px);
		width: calc(50% - 20px);
		margin-right: 0;
		margin-bottom: 47px;
	}

	@media (max-width: 660px) {
		max-width: calc(100% - 20px);
		width: calc(50% - 20px);
		margin-right: 0;
		margin-bottom: 27px;
	}

	@media (max-width: 550px) {
		max-width: calc(100% - 8px);
		width: calc(100% - 8px);
		margin-right: 0;
		margin-bottom: 27px;
	}
`;

export async function getStaticProps({ params }) {
	const { lang, url } = params;

	if (!url) return { notFound: true };

	const [page, allPosts, allTags, currentTag] = await Promise.all([
		fetchAPI('/blog-home', { lang, populate: ['promotedPosts'] }),
		fetchAPI('/blog-posts', {
			lang,
			sort: 'date:desc',
			limit: BLOG_POSTS_API_LIMIT,
		}),
		fetchAPI('/blog-posts-tags', {
			lang,
			limit: BLOG_POSTS_API_LIMIT,
		}),
		fetchAPI('/blog-posts-tags', {
			url,
			lang,
			populate: ['blogPosts', 'blogPosts.preview', 'blogPosts.article'],
		}),
	]);

	const tag = currentTag?.data?.[0];

	if (!tag) return { notFound: true };

	const recommended: any = [];

	page?.data?.promotedPosts
		?.map(({ url }) => url)
		?.forEach((url) => {
			allPosts?.data?.forEach((post) => {
				if (url === post.url) recommended.push(post);
			});
		});

	return {
		props: {
			url,
			tag,
			lang,
			recommended,
			tags: allTags?.data,
		},
	};
}

export async function getStaticPaths() {
	const entries = await fetchAPI('/blog-posts-tags');
	const paths: any = [];

	entries?.data?.forEach((entry) => {
		LANGUAGES.forEach((lang) => {
			paths.push({ params: { url: entry.url, lang: lang.code } });
		});
	});

	return {
		paths,
		fallback: false,
	};
}

export default TagPage;
